import { useState } from 'react';
import { Check, Wifi } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { CONSECUENCIA_RED, SIN_SENALES_RED, errorDeCambiarRed, motivoDeCambiarRed } from './cambiarDeRedUtil';
import { esDeProducto } from './estadoDeTerminalUtil';

/* =====================================================================
   «Cambiar de red» · TA-7 §6 (DISEÑO-TA7 §4.7 y §8; deuda TA7-2, P-28)
   =====================================================================
   `POST /api/terminales/{id}/ajustes-de-red` deja la orden
   ABRIR_AJUSTES_RED; la terminal la recoge en su siguiente latido y abre
   su pantalla de red sin pedir PIN durante 10 minutos. El servidor pide
   `terminal.administrar`, que esté vinculada y que no esté robada: esos
   tres casos apagan el botón CON su motivo a la vista (regla de la
   fachada). Sin señales sí se puede pedir: la orden espera a que vuelva.

   Confirmación con la consecuencia en una frase, el envío a la vista y lo
   que contestó el servidor en verde junto al botón. A 320 px la
   confirmación ocupa el renglón entero y nada flota (M1/M6).
   ===================================================================== */

const BOTON = 'inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50';

export default function CambiarDeRed({ t, puede = true, onHecho }) {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(null);
  const [error, setError] = useState(null);
  if (!esDeProducto(t) || !t.activo) return null;
  const motivo = motivoDeCambiarRed(t, puede);
  const id = `motivo-red-${t.id}`;

  async function pedir() {
    setEnviando(true);
    setError(null);
    try {
      const d = await api.post(`/terminales/${t.id}/ajustes-de-red`, {});
      const dice = d?.dice || 'En menos de un minuto la terminal pedirá la red nueva.';
      setHecho(dice);
      setConfirmando(false);
      onHecho?.({ mensaje: `«${t.nombre}»: ${dice}` });
    } catch (e) {
      setError(errorDeCambiarRed(e));
    } finally {
      setEnviando(false);
    }
  }

  if (confirmando) {
    return (
      <div className="basis-full w-full space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm"
           data-terminal="cambiar-red-confirmar">
        <p className="break-words text-amber-900">{CONSECUENCIA_RED}</p>
        {!t.en_linea && <p className="break-words text-amber-900">{SIN_SENALES_RED}</p>}
        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-red-900">
            <strong className="block break-words">{error.mensaje}</strong>
            <span className="break-words text-red-800">{error.queHacer}</span>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button type="button" onClick={pedir} disabled={enviando} aria-busy={enviando || undefined}
                  className={`${BOTON} justify-center bg-amber-600 text-white`}>
            <Check size={15} aria-hidden="true" />
            {enviando ? 'Enviando la orden…' : 'Sí, cambiar de red'}
          </button>
          <button type="button" onClick={() => { setConfirmando(false); setError(null); }} disabled={enviando}
                  className={`${BOTON} justify-center border border-gray-300 bg-white text-gray-700`}>
            No
          </button>
        </div>
      </div>
    );
  }

  return (
    <span className="inline-flex flex-col gap-1" data-terminal="cambiar-red">
      <span className="inline-flex items-center gap-1">
        <button type="button" onClick={() => { setHecho(null); setConfirmando(true); }}
                disabled={!!motivo} aria-describedby={motivo ? id : undefined}
                className={`${BOTON} border border-gray-300 bg-white text-gray-700`}>
          <Wifi size={15} aria-hidden="true" />
          Cambiar de red
        </button>
        <Ayuda id="terminal.red.boton" />
      </span>
      <Motivo id={id} cuando={!!motivo}>{motivo}</Motivo>
      {hecho && <p role="status" className="break-words text-xs font-semibold text-green-800">{hecho}</p>}
    </span>
  );
}
